import { IconButton, Tooltip } from '@mui/material';
import { Box } from '@mui/system';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import EditTwoToneIcon from '@mui/icons-material/EditTwoTone';
import { RootState } from 'src/redux-store/appStore';
import DeleteApplication from '../DeleteApplication';
import InfoIcon from '@mui/icons-material/Info';
import { handleError } from 'src/redux-store/slices/errorSlice';
import RejectApplication from '../RejectApplication';
import RemoveRedEyeIcon from '@mui/icons-material/RemoveRedEye';
import { shouldShowEdit } from 'src/utils/CommonFunctions';
// import DownloadForOfflineIcon from '@mui/icons-material/DownloadForOffline';
import PrintIcon from '@mui/icons-material/Print';
// import { downloadApplication } from './downloadApplication';
import { printApplication } from './printApplication';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import type { PetLicenseCartItem } from 'src/utils/petLicenseCart';
import {
  addPetLicenseToCart,
  selectPetLicenseCartItems
} from 'src/redux-store/slices/cartSlice';
import toast from 'react-hot-toast';
import { canUsePetLicenseBundleCheckout } from 'src/utils/petLicenseBundleEligibility';

interface ApplicationActionsProps {
  item: any;
  isDraft?: boolean;
  handleFetchApplications: () => void;
}

const ApplicationActions: React.FC<ApplicationActionsProps> = ({
  item,
  isDraft,
  handleFetchApplications
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const petLicenseCartItems = useSelector(selectPetLicenseCartItems);
  const currentPermitDetails = useSelector(
    (store: RootState) => store.currentPermit.currentPermitDetails
  );

  const isManager = currentPermitDetails.currentPermitUserRole === 'Manager';
  const userData = useSelector((store: RootState) => store.user.userDetails);
  const isManagerOrAdmin =
    isManager ||
    userData?.UserRole === 'Admin' ||
    userData?.UserRole === 'Super Admin';

  const showPetCartCta =
    currentPermitDetails?.id === 10 &&
    !isDraft &&
    item.ApplicationStatus === 'Approved' &&
    canUsePetLicenseBundleCheckout(
      currentPermitDetails.currentPermitUserRole,
      userData?.UserRole
    );

  const isPetInCart = petLicenseCartItems.some(
    (p) => p.ApplicationID === item.ApplicationID
  );

  const handleAddToPetLicenseCart = (): void => {
    const cartItem: PetLicenseCartItem = {
      ApplicationID: item.ApplicationID,
      ApplicationNumber: item.ApplicationNumber,
      PetName: item.PetName,
      PetType: item.PetType,
      OwnerName: item.OwnerName,
      UserID: item.UserID,
      IsSpayedNeutered: item.IsSpayedNeutered,
      ApplicationDate: item.ApplicationDate,
      CreateDate: item.CreateDate,
      StateRegistrationFee: item.StateRegistrationFee,
      PilotClinicFee: item.PilotClinicFee,
      LateFeesWaived: item.LateFeesWaived
    };
    if (isPetInCart) {
      toast.error('Already in cart');
      return;
    }
    dispatch(addPetLicenseToCart(cartItem));
    const label = item.PetName || item.ApplicationNumber || 'Application';
    toast.success(`${label} added to cart!`);
  };

  const handleMoveToEdit = (type?: string) => {
    navigate(
      `/${currentPermitDetails.townshipId}/${
        currentPermitDetails.departmentName
      }/${currentPermitDetails.id}/${type || 'edit'}-application/${
        item?.ApplicationID
      }`,
      { state: { isDraft, permitId: currentPermitDetails?.id } }
    );
  };

  const petCartButton = showPetCartCta ? (
    isPetInCart ? (
      <Tooltip title="In cart" arrow>
        <span>
          <IconButton
            color="success"
            size="small"
            disabled
            sx={{
              '&.Mui-disabled': {
                color: 'success.main',
                opacity: 1
              }
            }}
          >
            <CheckCircleOutlineIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
    ) : (
      <Tooltip title="Add to cart" arrow placement="top">
        <IconButton color="primary" size="small" onClick={handleAddToPetLicenseCart}>
          <ShoppingCartIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    )
  ) : null;

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        flexWrap: 'nowrap',
        gap: 0.75
      }}
    >
      <Tooltip title={'Edit'} arrow>
        <IconButton
          color="primary"
          disabled={shouldShowEdit(item)}
          onClick={() => handleMoveToEdit()}
        >
          {<EditTwoToneIcon fontSize="small" />}
        </IconButton>
      </Tooltip>

      {isDraft ? (
        <DeleteApplication
          item={item}
          refetchListData={handleFetchApplications}
        />
      ) : isManagerOrAdmin && (() => {
          if (currentPermitDetails?.id === 10) {
            return item.ApplicationStatus === 'Issued';
          }
          return (
            (item.ApplicationStatus === 'Approved' &&
              currentPermitDetails?.id !== 9) ||
            item.ApplicationStatus === 'Issued' ||
            item.ApplicationStatus === 'Closed Out'
          );
        })() ? (
        <Tooltip title="Print Application License" arrow>
          <IconButton
            color="success"
            // onClick={() =>
            //   downloadApplication(item?.ApplicationID, currentPermitDetails.id)
            onClick={() =>
  printApplication(item?.ApplicationID, currentPermitDetails.id)
            }
          >
            {/* <DownloadForOfflineIcon fontSize="small" /> */}
            <PrintIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ) : item.ApplicationStatus === 'Rejected' ? (
        <Tooltip title="Reason Of Rejection" arrow>
          <IconButton
            onClick={() =>
              dispatch(handleError(`Reason : ${item.ReasonOfRejection}`))
            }
          >
            <InfoIcon color="primary" />
          </IconButton>
        </Tooltip>
      ) : isManager ? (
        <RejectApplication
          item={item}
          refetchListData={handleFetchApplications}
        />
      ) : null}

      <Tooltip title="View" arrow>
        <IconButton color="info" onClick={() => handleMoveToEdit('view')}>
          <RemoveRedEyeIcon fontSize="small" />
        </IconButton>
      </Tooltip>

      {petCartButton}
    </Box>
  );
};

export default ApplicationActions;
